import classNames from 'classnames';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import flow from 'lodash/fp/flow';
import find from 'lodash/fp/find';
import get from 'lodash/fp/get';
import replace from 'lodash/fp/replace';

import { getGeoIPCode, splitNumberAndDialCode } from '@micro-apps/shared';
import './PhoneNumber.scss';

const getFormattedPhoneNumber = ({ dialCode, phoneNumber }) => {
	try {
		const phoneNumberBreakdown = parsePhoneNumberWithError(`+${dialCode}${phoneNumber}`);

		if (!phoneNumberBreakdown) return phoneNumber;

		const dialCodeRegex = new RegExp([`^\\+`, ...dialCode.split('')].join(`\\s*`) + `\\s*`);

		return phoneNumberBreakdown
			.formatInternational()
			.replace(dialCodeRegex, '')
			.replace(/\s+/g, '-');
	} catch (error) {
		return phoneNumber;
	}
};

export default function PhoneNumber({
	id: initialId,
	name,
	label,
	isRequired,
	placeholder,
	errorMessage = false,
	options: _options,
	value = '',
	onChange,
	onFocus = () => undefined,
	onBlur = () => undefined,
	countries,
	disabled,
	formatOptionLabel = ({ countryCode, dialCode, countryName }) => `+${dialCode} (${countryName})`,
	inputMode,
	autoComplete,
	autoCorrect,
}) {
	const { number: phoneNumberFromValue, dialCode: dialCodeFromValue } = splitNumberAndDialCode(
		value.replace(/^\++/, ''),
		countries
	);

	const dialCodes = countries.map(({ code, dialCode, title }) => ({
		countryCode: code,
		dialCode,
		countryName: title,
	}));

	const initialDialCode = flow(
		getGeoIPCode,
		geoIp => find({ countryCode: geoIp })(dialCodes),
		get('dialCode'),
		dialCode => dialCode || dialCodes[0].dialCode
	)();

	const dialCode = dialCodeFromValue || initialDialCode;
	const phoneNumber = phoneNumberFromValue;
	const formattedPhoneNumber = getFormattedPhoneNumber({ dialCode, phoneNumber });

	const handleCountryChange = e => {
		onChange({
			target: { value: `+${e.target.value}${phoneNumber}`, id: initialId, name },
			data: {
				phoneNumber,
				...(find({ dialCode: e.target.value })(dialCodes) || {}),
			},
		});
	};

	const handlePhoneNumberChange = flow(
		get(['target', 'value']),
		replace(new RegExp([`^\\+`, ...dialCode.split('')].join(`\\s*`) + `\\s*`), ''),
		replace(/-|\+|\s/g, ''),
		replace(/^0/, ''),
		nextPhoneNumber =>
			onChange({
				target: { value: `+${dialCode}${nextPhoneNumber}`, id: initialId, name },
				data: {
					phoneNumber: nextPhoneNumber,
					...(find({ dialCode })(dialCodes) || {}),
				},
			})
	);

	const hasError =
		(typeof errorMessage === 'boolean' && errorMessage === true) || errorMessage.length > 0;

	const placeholderToDisplay = isRequired ? (placeholder || label) + '*' : placeholder || label;

	const id = initialId || `field--${name}`;

	return (
		<div
			className={classNames(
				'phoneNumberContainer TextInput formElement formElement-text ValidatedForm-Field',
				{
					'fs-error-copy': hasError,
				}
			)}
		>
			<div className="phoneNumberContainer__dropdown ty-b2 Dropdown ValidatedForm-Field">
				<select
					className="Dropdown-field formElement-field ty-b2"
					name={[name, 'areaCode'].filter(Boolean).join('__')}
					id={[id, 'areaCode'].filter(Boolean).join('__')}
					value={dialCode}
					onChange={handleCountryChange}
					required={isRequired}
					disabled={disabled}
				>
					{dialCodes.map(dialCode => {
						return (
							<option key={dialCode.countryName} value={dialCode.dialCode}>
								{formatOptionLabel(dialCode)}
							</option>
						);
					})}
				</select>
				<div
					className={classNames('phoneNumberContainer__dropdownOverlay', {
						phoneNumberContainer__dropdownOverlay__error: hasError,
						'phoneNumberContainer__dropdownOverlay--disabled': disabled,
					})}
				>
					<span>{`+${dialCode}`}</span>
				</div>
			</div>
			<input
				className={classNames('TextInput-field formElement-field ty-b2', {
					'fs-error-copy': hasError,
				})}
				type="tel"
				name={name}
				id={id}
				value={formattedPhoneNumber}
				placeholder={placeholderToDisplay}
				onChange={handlePhoneNumberChange}
				onFocus={onFocus}
				onBlur={onBlur}
				required={isRequired}
				disabled={disabled}
				inputMode={inputMode}
				autoComplete={autoComplete}
				autoCorrect={autoCorrect}
			/>
			<label
				className={classNames('formElement-label ty-c4 floatingLabel', {
					'floatingLabel--showing': phoneNumber.length > 0,
					'formElement-label--invalid fs-error-copy': hasError,
				})}
				htmlFor={id}
			>
				{isRequired ? label + '*' : label}
			</label>
			{errorMessage && (
				<span className="formElement-message fs-error-copy" role="alert">
					{errorMessage}
				</span>
			)}
		</div>
	);
}
